import { Dispatch, SetStateAction, useEffect, useState } from "react"
import useSWR from "swr"
import { NewImage, SlicerData } from "pages/slicer/[id]"
import fetcher from "@utils/fetcher"
import { Message } from "@utils/handleMessage"
import { useAppContext } from "../context"
import { Button, MessageBlock } from "@components/ui"
import { useRouter } from "next/router"

type Props = {
  editMode: boolean
  setEditMode: Dispatch<SetStateAction<boolean>>
  slicerInfo: any
  slicer: SlicerData
  setSlicer: Dispatch<SetStateAction<SlicerData>>
  loading: boolean
  setLoading: Dispatch<SetStateAction<boolean>>
  newName: any
  setNewName: Dispatch<SetStateAction<string>>
  newPath: string
  setNewPath: Dispatch<SetStateAction<string>>
  newDescription: any
  setNewDescription: Dispatch<SetStateAction<any>>
  newTags: string
  setNewTags: Dispatch<SetStateAction<string>>
  newImage: NewImage
  setNewImage: Dispatch<SetStateAction<NewImage>>
  setTempImageUrl: Dispatch<SetStateAction<string>>
  msg: Message
  setMsg: Dispatch<SetStateAction<Message>>
}

const SlicerSubmitBlock = ({
  editMode,
  setEditMode,
  slicerInfo,
  slicer,
  setSlicer,
  loading,
  setLoading,
  newName,
  setNewName,
  newPath,
  setNewPath,
  newDescription,
  setNewDescription,
  newTags,
  setNewTags,
  newImage,
  setNewImage,
  setTempImageUrl,
  msg,
  setMsg
}: Props) => {
  const router = useRouter()
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL

  const { data: slicerInfoUpdated } = useSWR(
    editMode ? `/api/slicer/${slicerInfo?.id}?stats=false` : null,
    fetcher
  )

  const { account, provider, setModalView } = useAppContext()
  const [preventSubmit, setPreventSubmit] = useState(false)
  const [tempStorageUrl, setTempStorageUrl] = useState("")

  const openPopup = () => {
    setModalView({ name: "IRREVERSIBLE_VIEW" })
    setPreventSubmit(false)
  }

  const updateDb = async (newInfo: SlicerData) => {
    setSlicer(newInfo)
    const body = {
      method: "POST",
      body: JSON.stringify(newInfo)
    }
    await fetcher(`/api/slicer/${slicerInfo?.id}`, body)
  }

  const save = async () => {
    const { mutate } = await import("swr")
    const handleMessage = (await import("@utils/handleMessage")).default
    const slicerContract = (await import("@lib/initProvider")).slicer
    const supabaseUpload = (await import("@utils/supabaseUpload")).default

    setLoading(true)
    let newInfo: SlicerData = {
      name: newName,
      tags: newTags,
      description: newDescription,
      imageUrl: slicer.imageUrl,
      attributes: slicer.attributes,
      totalSlices: slicer.totalSlices,
      customPath: newPath,
      storeClosed: slicer.storeClosed
    }
    try {
      let isPayeeAllowed: boolean

      if (slicerInfo?.config?.creatorMetadata) {
        isPayeeAllowed =
          newInfo.attributes.filter((el) => el.trait_type === "Creator")[0]
            .value === account?.toLowerCase()
      } else {
        const contract = await slicerContract(slicerInfo?.id, provider)
        isPayeeAllowed = await contract.isPayeeAllowed(account)
      }

      if (!isPayeeAllowed) {
        throw Error("Payee is not allowed")
      }

      if (newImage.url) {
        setTempImageUrl(newImage.url)

        const { path } = await supabaseUpload(
          `${slicerInfo?.id}/main`,
          newImage,
          slicer.imageUrl,
          slicerInfo?.isImmutable
        )

        // Todo? If isImmutable store on web3Storage

        const newFilePath = `${supabaseUrl}/storage/v1/object/public/slicer-images/${path}`
        setTempStorageUrl(newFilePath)
        newInfo = {
          name: newName,
          tags: newTags,
          description: newDescription,
          imageUrl: newFilePath,
          attributes: slicer.attributes,
          totalSlices: slicer.totalSlices,
          customPath: newPath,
          storeClosed: slicer.storeClosed
        }
        setNewImage({ url: "", file: undefined })
      }

      await updateDb(newInfo)
      mutate(`/api/slicer/${slicerInfo?.id}?stats=false`)
      await fetcher(`/api/slicer/${slicerInfo.id}/refresh`)
      if (slicer.customPath != newInfo.customPath) {
        if (newInfo.customPath) {
          await fetcher(`/api/slicer/${newInfo.customPath}/refresh`)
        }
        router.push(`/slicer/${newInfo.customPath || String(slicerInfo.id)}`)
      }
      setEditMode(false)
      setLoading(false)
    } catch (err) {
      console.log(err.message)
      setLoading(false)
      handleMessage(
        {
          message:
            err.message === "Payee is not allowed"
              ? err.message
              : "Something went wrong, try again",
          messageStatus: "error"
        },
        setMsg
      )
    }
  }

  const cancel = () => {
    setNewName(slicer.name)
    setNewPath(slicer.customPath)
    setNewDescription(slicer.description)
    setNewTags(slicer.tags)
    setNewImage({ url: "", file: undefined })
    setEditMode(false)
  }

  useEffect(() => {
    if (slicerInfo?.isImmutable) {
      setPreventSubmit(true)
    }
  }, [slicerInfo])

  useEffect(() => {
    if (!tempStorageUrl && slicerInfoUpdated?.imageUrl) {
      setTempStorageUrl(slicerInfoUpdated?.imageUrl)
    }
  }, [slicerInfoUpdated])

  return (
    <div>
      <div className="py-12">
        <Button
          label="Save"
          loading={loading}
          onClick={() => (preventSubmit ? openPopup() : save())}
        />
      </div>
      {!loading && (
        <p
          className="inline-block font-medium text-red-500 cursor-pointer hover:underline"
          onClick={() => cancel()}
        >
          Cancel
        </p>
      )}
      <MessageBlock msg={msg} />
    </div>
  )
}

export default SlicerSubmitBlock
